/* eslint-disable @typescript-eslint/dot-notation */
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Forbid, Trash, Plus } from '@saleshandy/icons';
import Tabs, { TabPane } from 'rc-tabs';
import { Dropdown } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ProspectSequenceListTable from './prospect-sequence-list-table';
import ProspectProfileForm from './prospect-profile-form';
import { RequestStatus } from '../../../../../../../shared/enums/request-status';
import Modal from '../../../../../../../shared/design-system/components/atoms/modal';
import { SelectedProspectData } from '../../../../../../sequence/types';
import { getIsRequestPending } from '../../../../../../../shared/utils/get-request-status';
import { Field } from '../../../../../../settings/types/field';
import { OverlayTooltip } from '../../../../../../../shared/design-system/components/overlay';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import AddRemoveTag from '../../../../../../../shared/components/add-remove-tag';
import {
  AddTagsToProspectsPayload,
  ProspectFieldPayload,
  UpdateProspectPinStatusProps,
} from '../../../../../types';
import { executeOnRequestStatus } from '../../../../../../../shared/utils';
import ProspectDetailActivity from './prospect-detail-activity';
import TasksListTab from './tasks-list-tab';
import { mapSequenceStatusToContactFilter } from '../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import {
  extractProspectAttributes,
  getFirstName,
  getLastName,
} from './helpers/prospect-attributes';
import { useProspectProfileFields } from './hooks/use-prospect-profile-fields';
import ProspectProfileSection from './components/prospect-profile-section-container';
import NotesListTab from './notes-list-tab/notes-list-tab-container';
import CallsListTab from './calls-list-tab/call-list-tab-container';
import EmailListTab from './email-list-tab';
import { RootState } from '../../../../../../../store/root-reducer';
import { useProspectDetailsUrl } from '../../../../../../../shared/hooks/use-prospect-details-url';
import type { ValidTab } from '../../../../../../../shared/utils/prospect-details-url';
import { getSingleProspectDetails } from '../../../../../helpers/prospect.api';
import ProspectDetailsTabTitle from './components/prospect-details-tab-title';
import type { CallLog } from '../../../../../../dialer/types/call-logs';
import { PhoneNumberListResponse } from '../../../../../../dialer/types/phone-numbers';

enum DropDownActionOptions {
  Unsubscribe = 'unsubscribe',
  Remove = 'remove',
}

export type IProps = {
  show: boolean;
  onHide: () => void;
  selectedProspectId: number;
  getSingleProspectSequencesDetailsStatus: RequestStatus;
  getSingleProspectDetailsStatus: RequestStatus;
  saveContactAttributesRequestStatus: RequestStatus;
  viewAddToSequenceModal: () => void;
  unsubscribeProspectRequest: () => void;
  removeProspectRequest: () => void;
  sendGetSingleProspectSequencesDetailsRequest: () => void;
  prospectSequenceRemoveFromSequence: (sequenceId: number) => void;
  prospectSequenceMarkAsReplied: (stepId: number) => void;
  prospectSequenceMarkAsFinished: (stepId: number) => void;
  prospectSequenceAddToStep: (sequenceId, stepId) => void;
  sendSaveContactAttributesRequest: (contactId, attributes) => void;
  selectedProspectData: SelectedProspectData;
  selectedProspectSequences: any[];
  sendGetSingleProspectDetailsRequest: (id: number) => void;
  isSingleProspectSubscribed: boolean;
  t: (x: string) => ReactNode;
  viewTagsModal: () => void;
  sendPrimaryEmailChangeInProspectRequest: (
    primaryEmailProspectInfoId: number,
    contactId: number,
  ) => void;
  sendPrimaryPhoneChangeInProspectRequest: (
    primaryPhoneProspectInfoId: number,
    contactId: number,
  ) => void;
  sendDeleteEmailFromProspectRequest: (
    prospectInfoId: number,
    contactId: number,
  ) => void;
  sendDeletePhoneFromProspectRequest: (
    prospectInfoId: number,
    contactId: number,
  ) => void;
  deleteSecondaryEmailRequestStatus?: string;
  resetProspectDeleteEmailRequestStatus?: () => void;
  deleteSecondaryPhoneRequestStatus?: string;
  resetProspectDeletePhoneRequestStatus?: () => void;
  resetProspectPrimaryEmailChangeRequestStatus?: () => void;
  resetUpdatePrimaryNumberState?: () => void;
  primaryEmailChangeRequestStatus?: RequestStatus;
  primaryPhoneChangeRequestStatus?: RequestStatus;
  allOutcomes: any[];
  sendGetTagsRequest?: () => void;
  sendAddTagsToProspectsRequest?: (payload: AddTagsToProspectsPayload) => void;
  resetTags?: () => void;
  tags?: any[];
  addTagsToProspectsRequestStatus?: RequestStatus;
  fields?: Field[];
  defaultTab?: 'activity' | 'sequences' | 'contacts';
  sendGetTasksRequest?: (params: any) => void;
  getTasksRequestStatus?: RequestStatus;
  tasks?: any[];
  sendUpdateTasksRequest?: (payload: any) => void;
  taskActionRequestStatus?: RequestStatus;
  taskActionRequestMessage?: string;
  sendGetUndoTasksRequest?: (params: any, taskId: number) => void;
  getUndoTasksRequestStatus?: RequestStatus;
  sendCreateTaskRequest?: (payload: any) => void;
  createTaskRequestStatus?: RequestStatus;
  createTaskRequestMessage?: string;
  createTaskRequestError?: any;
  sendGetTaskAssigneeListRequest?: () => void;
  taskAssigneeList?: any[];
  userId?: number;
  sendUploadAttachmentRequest?: (payload: any) => void;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: any;
  uploadedAttachment?: any;
  sendDeleteAttachmentRequest?: (payload: any) => void;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: any;
  deletedAttachmentId?: number;
  sendImageUploadRequest?: (payload: any) => void;
  resetUploadAttachmentRequest?: () => void;
  resetDeleteAttachmentRequest?: () => void;
  resetCreateTaskRequest?: () => void;
  sendGetShareableTaskerDataRequest?: (hashId: { hashId: string }) => void;
  getShareableTaskerDataRequestStatus?: RequestStatus;
  shareableTaskerData?: any;
  resetShareableTaskerState?: () => void;
  sendGetWhatsappLinkRequest?: (taskId: number) => void;
  getWhatsappLinkRequestStatus?: RequestStatus;
  getWhatsappLinkResponse?: string;
  resetGetWhatsappLinkState?: () => void;
  setShowGlobalRestrictionModal?: (type: any) => void;
  sendCreateProspectNoteRequest?: (payload: any) => void;
  createProspectNoteRequestStatus?: RequestStatus;
  createProspectNoteRequestMessage?: string;
  createProspectNoteResponse?: any;
  resetCreateProspectNotesListRequest?: () => void;
  sendUpdateProspectNoteRequest?: (payload: any) => void;
  updateProspectNoteRequestStatus?: RequestStatus;
  resetUpdateProspectNoteRequest?: () => void;
  getFieldsRequest?: () => void;
  getSinglePropsectDetailPinnedFieldsRequest?: () => void;
  updateProspectPinStatusRequest?: (
    payload: UpdateProspectPinStatusProps,
  ) => void;
  sendUpdateProspectFieldByIdRequest?: (payload: ProspectFieldPayload) => void;
  disabledModalNavArrows?: ('left' | 'right')[];
  onModalNavArrowClick?: (direction: 'left' | 'right') => void;
  showProspectNaviagateButtons?: boolean;
  getSingleProspect?: any;
  resetUpdateProspectFieldByIdRequest?: () => void;
  getSequenceListRequest?: () => void;
  getStepListRequest?: ({ sequenceId }) => void;
  addToStepRequestStatus?: RequestStatus;
  addToStepRequestSuccessfulCount?: number;
  addToStepRequestFailedCount?: number;
  addToStepRequestMessage?: string;
  sendRemoveTagsFromProspectsRequest?: (payload: any) => void;
  removeTagsFromProspectsRequestStatus?: RequestStatus;
  sendRemoveTagsFromProspectsRequestStatus?: RequestStatus;
  sendAddTagsToProspectsRequestStatus?: RequestStatus;
  sendGetProspectActivityCountRequest?: (prospectId: number) => void;
  getProspectActivityCountRequestStatus?: RequestStatus;
  editProspectAcitivityCount?: (payload: any) => void;
  isDemoAccount?: boolean;
  /** Call Logs dialer: custom header + expand/collapse tabs via modal arrows */
  callLogsVariant?: boolean;
  callLogRow?: CallLog | null;
  callLogsProfileHeader?: ReactNode;
  prospectDetail?: string;
  myPhoneNumbersList?: PhoneNumberListResponse;
};

type DropDownAction = {
  id: number;
  key: string;
  displayName: ReactNode;
  isSVGIcon?: boolean;
  SVGIcon?: ReactNode;
};

const ProspectDetailsModal: React.FC<IProps> = ({
  show,
  onHide,
  selectedProspectId,
  selectedProspectData,
  selectedProspectSequences,
  getSingleProspectSequencesDetailsStatus,
  viewAddToSequenceModal,
  unsubscribeProspectRequest,
  removeProspectRequest,
  prospectSequenceMarkAsReplied,
  sendGetSingleProspectSequencesDetailsRequest,
  prospectSequenceRemoveFromSequence,
  prospectSequenceAddToStep,
  sendSaveContactAttributesRequest,
  sendGetSingleProspectDetailsRequest,
  getSingleProspectDetailsStatus,
  saveContactAttributesRequestStatus,
  isSingleProspectSubscribed,
  prospectSequenceMarkAsFinished,
  sendPrimaryEmailChangeInProspectRequest,
  sendPrimaryPhoneChangeInProspectRequest,
  sendDeleteEmailFromProspectRequest,
  sendDeletePhoneFromProspectRequest,
  deleteSecondaryEmailRequestStatus,
  resetProspectDeleteEmailRequestStatus,
  deleteSecondaryPhoneRequestStatus,
  resetProspectDeletePhoneRequestStatus,
  resetProspectPrimaryEmailChangeRequestStatus,
  resetUpdatePrimaryNumberState,
  primaryEmailChangeRequestStatus,
  primaryPhoneChangeRequestStatus,
  t,
  allOutcomes,
  sendGetTagsRequest,
  sendAddTagsToProspectsRequest,
  resetTags,
  tags: prospectTags = [],
  addTagsToProspectsRequestStatus,
  fields,
  defaultTab = 'activity',
  sendGetTasksRequest,
  getTasksRequestStatus,
  tasks,
  sendUpdateTasksRequest,
  taskActionRequestStatus,
  taskActionRequestMessage,
  sendGetUndoTasksRequest,
  getUndoTasksRequestStatus,
  sendCreateTaskRequest,
  createTaskRequestStatus,
  createTaskRequestMessage,
  createTaskRequestError,
  sendGetTaskAssigneeListRequest,
  taskAssigneeList,
  userId,
  sendUploadAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  sendDeleteAttachmentRequest,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  sendImageUploadRequest,
  resetUploadAttachmentRequest,
  resetDeleteAttachmentRequest,
  resetCreateTaskRequest,
  sendGetShareableTaskerDataRequest,
  getShareableTaskerDataRequestStatus,
  shareableTaskerData,
  resetShareableTaskerState,
  sendGetWhatsappLinkRequest,
  getWhatsappLinkRequestStatus,
  getWhatsappLinkResponse,
  resetGetWhatsappLinkState,
  setShowGlobalRestrictionModal,
  sendCreateProspectNoteRequest,
  createProspectNoteRequestStatus,
  createProspectNoteRequestMessage,
  createProspectNoteResponse,
  resetCreateProspectNotesListRequest,
  sendUpdateProspectNoteRequest,
  updateProspectNoteRequestStatus,
  resetUpdateProspectNoteRequest,
  getFieldsRequest,
  getSinglePropsectDetailPinnedFieldsRequest,
  updateProspectPinStatusRequest,
  sendUpdateProspectFieldByIdRequest,
  disabledModalNavArrows = [],
  onModalNavArrowClick = () => {},
  showProspectNaviagateButtons = false,
  getSingleProspect,
  resetUpdateProspectFieldByIdRequest,
  getSequenceListRequest,
  getStepListRequest,
  addToStepRequestStatus,
  addToStepRequestSuccessfulCount,
  addToStepRequestFailedCount,
  addToStepRequestMessage,
  sendRemoveTagsFromProspectsRequest,
  removeTagsFromProspectsRequestStatus,
  sendAddTagsToProspectsRequestStatus,
  sendRemoveTagsFromProspectsRequestStatus,
  sendGetProspectActivityCountRequest,
  editProspectAcitivityCount,
  isDemoAccount,
  callLogsVariant = false,
  callLogRow = null,
  callLogsProfileHeader = null,
  prospectDetail = null,
  myPhoneNumbersList = null,
}) => {
  const embeddedDrawerLayout = callLogsVariant;
  const hasCallLogsProspectId = selectedProspectId > 0;
  const [callLogsSheetExpanded, setCallLogsSheetExpanded] = useState(true);
  /** Keeps tab content mounted briefly after collapse so drawer width can animate (matches _prospect.scss transition). */
  const [
    keepCallLogsTabsDuringCollapse,
    setKeepCallLogsTabsDuringCollapse,
  ] = useState(false);

  const isLoading = getIsRequestPending(getSingleProspectDetailsStatus);
  const [fetchActivity, setFetchActivity] = useState(false);
  const [fetchNotes, setFetchNotes] = useState(false);
  const [fetchTasks, setFetchTasks] = useState(false);
  const tagRef = useRef(null);
  const { changeTab, tabFromUrl } = useProspectDetailsUrl();

  // Determine the effective default tab (prioritize URL, then prop, then fallback)
  const effectiveDefaultTab = useMemo(() => {
    if (tabFromUrl) {
      return tabFromUrl;
    }
    return defaultTab || 'activity';
  }, [tabFromUrl, defaultTab]);

  const [activeTab, setActiveTab] = useState<string>(
    () => tabFromUrl || defaultTab || 'activity',
  );
  const [showModal, setShowModal] = useState(false);
  const [shouldOpenAddTaskForm, setShouldOpenAddTaskForm] = useState(false);
  const [shouldOpenEmailEditor, setShouldOpenEmailEditor] = useState(false);
  const [shouldExpandAddSequence, setShouldExpandAddSequence] = useState(false);
  const [tabProspectMap, setTabProspectMap] = useState<Record<string, number>>(
    {},
  );
  const [callsTabCount, setCallsTabCount] = useState(0);

  const getProspectIdForTab = (tabKey: string): number => {
    if (tabKey === activeTab) {
      return selectedProspectId;
    }
    return tabProspectMap[tabKey] || selectedProspectId;
  };

  /**
   * Generate key for tab content to force remount when prospect changes.
   * Active tab always uses current prospectId, inactive tabs keep their last prospectId.
   */
  const getActiveTabContentKey = (tabKey: string) => {
    const prospectId =
      tabKey === activeTab
        ? selectedProspectId
        : tabProspectMap[tabKey] || selectedProspectId;
    return `${tabKey}-${prospectId}`;
  };

  // Update prospect ID for active tab when it changes
  useEffect(() => {
    if (activeTab) {
      setTabProspectMap((prev) => ({
        ...prev,
        [activeTab]: selectedProspectId,
      }));
    }
  }, [activeTab, selectedProspectId]);

  // Clear tracking when modal closes
  useEffect(() => {
    if (!show) {
      setTabProspectMap({});
    }
  }, [show]);

  // Sync activeTab with effectiveDefaultTab when modal opens or when effectiveDefaultTab changes
  useEffect(() => {
    if (show && effectiveDefaultTab) {
      setActiveTab(effectiveDefaultTab);
    }
    // Reset add task form flag when modal closes
    if (!show) {
      setShouldOpenAddTaskForm(false);
      setShouldOpenEmailEditor(false);
      setShouldExpandAddSequence(false);
      setFetchActivity(false);
      setFetchNotes(false);
      setFetchTasks(false);
    }
  }, [show, selectedProspectId, effectiveDefaultTab]);

  useEffect(() => {
    setFetchActivity(false);
    setFetchNotes(false);
    setFetchTasks(false);
    setCallsTabCount(0);
  }, [selectedProspectId]);

  // Sync activeTab when tabFromUrl changes (URL-based navigation)
  // This handles direct URL access and browser back/forward
  // Only sync if tabFromUrl is different from current activeTab to avoid overriding user clicks
  useEffect(() => {
    if (show && tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl, show]);

  useEffect(() => {
    if (show && embeddedDrawerLayout && hasCallLogsProspectId) {
      setCallLogsSheetExpanded(true);
    }
  }, [show, embeddedDrawerLayout, selectedProspectId, hasCallLogsProspectId]);

  useEffect(() => {
    if (!show) {
      setCallLogsSheetExpanded(true);
    }
  }, [show]);

  useEffect(() => {
    if (
      callLogsSheetExpanded ||
      !embeddedDrawerLayout ||
      !hasCallLogsProspectId
    ) {
      setKeepCallLogsTabsDuringCollapse(false);
      return undefined;
    }
    setKeepCallLogsTabsDuringCollapse(true);
    const collapseTransitionTimeoutId = window.setTimeout(() => {
      setKeepCallLogsTabsDuringCollapse(false);
    }, 350);
    return () => clearTimeout(collapseTransitionTimeoutId);
  }, [callLogsSheetExpanded, embeddedDrawerLayout, hasCallLogsProspectId]);

  const isCallLogsDrawerCollapsed =
    embeddedDrawerLayout && (!hasCallLogsProspectId || !callLogsSheetExpanded);

  const showCallLogsTabsSection = useMemo(
    () =>
      !embeddedDrawerLayout ||
      (hasCallLogsProspectId &&
        (callLogsSheetExpanded || keepCallLogsTabsDuringCollapse)),
    [
      embeddedDrawerLayout,
      hasCallLogsProspectId,
      callLogsSheetExpanded,
      keepCallLogsTabsDuringCollapse,
    ],
  );

  const showCallLogsExpandCollapseArrows =
    embeddedDrawerLayout && hasCallLogsProspectId;

  const mergedDisabledModalNavArrows = useMemo((): ('left' | 'right')[] => {
    if (
      showCallLogsExpandCollapseArrows &&
      (!showProspectNaviagateButtons || callLogsVariant)
    ) {
      return callLogsSheetExpanded ? ['left'] : ['right'];
    }
    return disabledModalNavArrows;
  }, [
    showCallLogsExpandCollapseArrows,
    showProspectNaviagateButtons,
    callLogsVariant,
    callLogsSheetExpanded,
    disabledModalNavArrows,
  ]);

  const handleModalNavClick = useCallback(
    (direction: 'left' | 'right') => {
      // Dialer: arrows only expand/collapse; never CRM prev/next (stale Redux can still flip flags briefly).
      if (
        showCallLogsExpandCollapseArrows &&
        (!showProspectNaviagateButtons || callLogsVariant)
      ) {
        if (direction === 'left') setCallLogsSheetExpanded(true);
        if (direction === 'right') setCallLogsSheetExpanded(false);
        return;
      }
      onModalNavArrowClick(direction);
    },
    [
      showCallLogsExpandCollapseArrows,
      showProspectNaviagateButtons,
      callLogsVariant,
      onModalNavArrowClick,
    ],
  );

  const showCombinedModalNavArrows = callLogsVariant
    ? showCallLogsExpandCollapseArrows
    : showProspectNaviagateButtons;

  // Get singleProspectResponse from Redux
  const singleProspectResponse = useSelector(
    (state: RootState) => state?.prospect?.getSingleProspect,
  );

  const changePrimaryEmailInProspectRequestStatus = useSelector(
    (state: RootState) =>
      state.prospect.changePrimaryEmailInProspectRequest.status,
  );

  const updatePrimaryNumberRequestStatus = useSelector(
    (state: RootState) => state.prospect.updatePrimaryNumberRequest.status,
  );

  const prospectActivityCount = useSelector(
    (state: RootState) => state.prospect?.prospectActivityCount,
  );

  useEffect(() => {
    const activityCallsCount = Number(prospectActivityCount?.calls) || 0;
    if (activityCallsCount > 0) {
      setCallsTabCount(activityCallsCount);
    }
  }, [prospectActivityCount?.calls]);

  const isDialerFeatureEnabled = useSelector(
    (state: RootState) => state.home.isDialerFeatureEnabled,
  );

  const isAgencyUser = useSelector(
    (state: RootState) => state.home.isAgencyUser,
  );

  const isDialerAvailable = isDialerFeatureEnabled && !isAgencyUser;

  // Normalize singleProspectResponse to always be an array (same as in prospect-profile-section.tsx)
  const prospectData = useMemo(
    () =>
      Array.isArray(singleProspectResponse)
        ? singleProspectResponse[0] || null
        : singleProspectResponse || null,
    [singleProspectResponse],
  );

  const prospectOutcomeSequences = useMemo(
    () =>
      getSingleProspect?.sequenceProspects?.map((item) => {
        const currentStep = item?.currentStep || item;
        const sequenceProspect = item?.sequenceProspect || item;

        return {
          id: currentStep?.sequence?.id ?? item?.sequence?.id,
          title: currentStep?.sequence?.title ?? item?.sequence?.title ?? '',
          status: mapSequenceStatusToContactFilter(item?.finalStatus) || '',
          categoryId: sequenceProspect?.categoryId ?? item?.categoryId,
        };
      }) || [],
    [getSingleProspect],
  );

  const prospectOutcomeSubsequences = useMemo(
    () =>
      getSingleProspect?.sequenceProspects
        ?.filter((item) => {
          const currentStep = item?.currentStep || item;
          return currentStep?.subsequenceData;
        })
        ?.map((item) => {
          const currentStep = item?.currentStep || item;
          const sequenceProspect = item?.sequenceProspect || item;

          return {
            id: currentStep?.subsequenceData?.id,
            title: currentStep?.subsequenceData?.title ?? '',
            status: mapSequenceStatusToContactFilter(item?.finalStatus) || '',
            categoryId: sequenceProspect?.categoryId ?? item?.categoryId,
            parentSequenceId: currentStep?.subsequenceData?.parentSequenceId,
          };
        }) || [],
    [getSingleProspect],
  );

  const onActivityChange = (value: boolean) => {
    setFetchActivity(value);
  };

  const onNoteChange = (value: boolean) => {
    setFetchNotes(value);
  };

  const onTaskChange = (value: boolean) => {
    setFetchTasks(value);
  };

  const {
    filteredProspectProfileFields,
    pinnedFieldIds,
    editingFieldId,
    handlePinToggle,
    handlePinKeyDown,
    handleEditClick,
    handleEditKeyDown,
  } = useProspectProfileFields({
    prospectOutcomeSequences,
    prospectOutcomeSubsequences,
    selectedProspectId,
    allOutcomes,
    prospectData,
    sendUpdateProspectFieldByIdRequest,
    getSinglePropsectDetailPinnedFieldsRequest,
    updateProspectPinStatusRequest,
    fields,
    sendGetSingleProspectDetailsRequest,
    resetUpdateProspectFieldByIdRequest,
    setActiveTab,
    changeTab,
    onActivityChange,
  });

  const renderAddAction = () => (
    <OverlayTooltip text="Add">
      <Plus width={20} height={20} className="prospect-profile-add" />
    </OverlayTooltip>
  );

  const dropDownListOptions = [
    hasPermission(Permissions.SEQUENCE_PROSPECT_STATUS_UNSUBSCRIBE) && {
      id: 1,
      key: DropDownActionOptions.Unsubscribe,
      displayName: t('labels.unsubscribe'),
      isSVGIcon: true,
      SVGIcon: <Forbid className="gray-txt-15 mr-1" />,
      permission: Permissions.SEQUENCE_PROSPECT_STATUS_UNSUBSCRIBE,
    },
    hasPermission(Permissions.SEQUENCE_PROSPECT_REMOVE) && {
      id: 2,
      key: DropDownActionOptions.Remove,
      displayName: t('labels.remove_delete'),
      isSVGIcon: true,
      SVGIcon: <Trash className="gray-txt-15 mr-1" />,
      permission: Permissions.SEQUENCE_PROSPECT_REMOVE,
    },
  ];

  const onDropdownActionSelect = (actionKey: string) => {
    switch (actionKey) {
      case DropDownActionOptions.Unsubscribe:
        unsubscribeProspectRequest();
        break;

      case DropDownActionOptions.Remove:
        removeProspectRequest();
        break;
      default:
        break;
    }
  };

  const generateDropdownItemsListFromActions = (actions: DropDownAction[]) =>
    actions !== null
      ? actions.map((action) => (
          <Dropdown.Item
            onClick={() => onDropdownActionSelect(action.key)}
            key={action.id}
            bsPrefix="dropdown-item-custom"
          >
            {action.SVGIcon}
            {action.displayName && (
              <span className="gray-txt-10">{action.displayName}</span>
            )}
          </Dropdown.Item>
        ))
      : null;

  const dropDownActions = generateDropdownItemsListFromActions(
    dropDownListOptions,
  );

  const prospectAttributes = useMemo(
    () => extractProspectAttributes(selectedProspectData),
    [selectedProspectData],
  );

  const firstName = useMemo(() => getFirstName(prospectAttributes), [
    prospectAttributes,
  ]);
  const lastName = useMemo(() => getLastName(prospectAttributes), [
    prospectAttributes,
  ]);

  const handleSaveContactAttributesSubmit = async (attributes) => {
    const res: any = await sendSaveContactAttributesRequest(
      selectedProspectId,
      attributes,
    );
    if (!res.error) {
      sendGetSingleProspectDetailsRequest(selectedProspectId);
    }
  };

  const handleAddTag = async (newTags) => {
    setShowModal(false);
    sendAddTagsToProspectsRequest({
      ids: [],
      newTags: newTags.map((tag) => tag.name),
      prospects: [selectedProspectId],
    });
  };

  const handleRemoveTag = (removedTags) => {
    setShowModal(false);
    const removedTagsPayload = removedTags.map((tag) => ({
      tagId: tag.id,
      prospects: [selectedProspectId],
    }));
    sendRemoveTagsFromProspectsRequest(removedTagsPayload);
  };

  const renderAddTag = () =>
    hasPermission(Permissions.SEQUENCE_PROSPECT_UPDATE_TAGS) ? (
      <AddRemoveTag
        ref={tagRef}
        handleAddTag={handleAddTag}
        handleRemoveTag={handleRemoveTag}
        tags={prospectTags}
        selectedRecords={selectedProspectData ? [selectedProspectData] : []}
        isSubmitLoading={
          getIsRequestPending(sendAddTagsToProspectsRequestStatus) ||
          getIsRequestPending(sendRemoveTagsFromProspectsRequestStatus) ||
          getIsRequestPending(addTagsToProspectsRequestStatus) ||
          getIsRequestPending(removeTagsFromProspectsRequestStatus)
        }
        setShowModal={setShowModal}
        showModal={showModal}
        isShowOnlyTagIcon={true}
      />
    ) : (
      <></>
    );

  useEffect(() => {
    if (showModal) {
      sendGetTagsRequest();
    }
  }, [showModal]);
  useEffect(() => {
    if (fields?.length === 0) {
      getFieldsRequest();
    }
  }, [fields]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addTagsToProspectsRequestStatus,
      onSuccess: () => {
        resetTags();
      },
    });
  }, [addTagsToProspectsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: changePrimaryEmailInProspectRequestStatus,
      onSuccess: () => {
        getSingleProspectDetails(selectedProspectId);
      },
    });
  }, [changePrimaryEmailInProspectRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updatePrimaryNumberRequestStatus,
      onSuccess: () => {
        getSingleProspectDetails(selectedProspectId);
      },
    });
  }, [updatePrimaryNumberRequestStatus]);

  // ** Fetch Count of Tasks, Notes, Emails, Sequences
  useEffect(() => {
    if (selectedProspectId) {
      sendGetProspectActivityCountRequest(selectedProspectId);
    }
  }, [selectedProspectId]);

  const defaultProspectProfileSection = (
    <ProspectProfileSection
      isCallQuickActionDisabled={myPhoneNumbersList?.total === 0}
      filteredProspectProfileFields={filteredProspectProfileFields}
      pinnedFieldIds={pinnedFieldIds}
      editingFieldId={editingFieldId}
      onPinToggle={handlePinToggle}
      onPinKeyDown={handlePinKeyDown}
      onEditClick={handleEditClick}
      onEditKeyDown={handleEditKeyDown}
      renderAddAction={renderAddAction}
      selectedProspectId={selectedProspectId}
      selectedProspectData={selectedProspectData}
      firstName={firstName}
      lastName={lastName}
      viewAddToSequenceModal={viewAddToSequenceModal}
      isLoading={isLoading}
      renderAddTag={renderAddTag}
      dropDownActions={dropDownActions}
      setActiveTab={setActiveTab}
      changeTab={changeTab}
      activeTab={activeTab}
      onTaskIconClick={() => setShouldOpenAddTaskForm(true)}
      onEmailIconClick={() => setShouldOpenEmailEditor(true)}
      onAddSequenceExpanded={() => setShouldExpandAddSequence(true)}
      onActivityChange={setFetchActivity}
    />
  );

  const urlSyncEntityId =
    callLogsVariant && callLogRow?.id ? callLogRow?.id : selectedProspectId;

  const handleCallLogsViewActivityClick = useCallback(() => {
    if (!urlSyncEntityId) return;
    if (!hasPermission(Permissions.SEQUENCE_PROSPECT_ACTIVITY)) return;
    const activityTab: ValidTab = 'activity';
    setActiveTab(activityTab);
    changeTab(urlSyncEntityId, activityTab);
    setFetchActivity(true);
    setCallLogsSheetExpanded(true);
  }, [urlSyncEntityId, changeTab, setCallLogsSheetExpanded]);

  const renderEmbeddedProfileHeader = (): ReactNode => {
    const header = callLogsProfileHeader;
    if (!header) return null;
    if (React.isValidElement(header)) {
      return React.cloneElement(header, {
        onViewActivityClick: handleCallLogsViewActivityClick,
        selectedProspectData,
      } as any);
    }
    return header;
  };

  const renderModalTopSection = (): ReactNode => {
    if (!embeddedDrawerLayout) {
      return defaultProspectProfileSection;
    }
    if (!hasCallLogsProspectId) {
      if (callLogsProfileHeader) {
        return renderEmbeddedProfileHeader();
      }
      if (callLogsVariant && callLogRow) {
        return (
          <div className="prospect-modal-call-logs-fallback regular-2 gray-txt-11 p-3">
            <div className="semibold-4 gray-txt-9">
              {callLogRow.prospectName?.trim() || '—'}
            </div>
            {callLogRow.prospectPhone?.number && (
              <div className="mt-1">{callLogRow.prospectPhone.number}</div>
            )}
          </div>
        );
      }
      return null;
    }
    if (callLogsProfileHeader) {
      return renderEmbeddedProfileHeader();
    }
    return defaultProspectProfileSection;
  };

  return (
    <>
      <Modal
        show={show}
        className={`prospect-details-modal prospect-modal ${
          // eslint-disable-next-line no-nested-ternary
          isCallLogsDrawerCollapsed
            ? 'prospect-modal--call-logs-collapsed'
            : prospectDetail === 'Call Logs'
            ? ' prospect-modal--call-logs'
            : ''
        }`}
        titleContent=""
        onClose={onHide}
        // onHide={onHide}
        showCloseIcon={true}
        isCustomCloseIcon={true}
        hideFooter={true}
        extraModalProps={{
          enforceFocus: false,
          'aria-labelledby': 'contained-modal-title-vcenter',
        }}
        footerContent={false}
        backdrop="static"
        showModalNavArrows={showCombinedModalNavArrows}
        onModalNavArrowClick={handleModalNavClick}
        disabledModalNavArrows={mergedDisabledModalNavArrows}
        drawerVariant={embeddedDrawerLayout ? 'call-logs' : 'prospect'}
        callLogsSheetExpanded={callLogsSheetExpanded}
      >
        <div className="prospect-detail-modal-container">
          {renderModalTopSection()}
          {showCallLogsTabsSection && (
            <div
              className={`prospect-details-container ${
                activeTab === 'contacts' ? 'prospect-all-fields-tab' : ''
              }`}
            >
              <div
                className={`prospect-tab-action ${
                  ['notes', 'tasks', 'calls']?.includes(activeTab)
                    ? 'prospect-tab-notes-tab-action'
                    : ''
                }`}
              >
                <Tabs
                  activeKey={activeTab || effectiveDefaultTab}
                  prefixCls="bs-tabs"
                  className="bs-tabs-small"
                  destroyInactiveTabPane={false}
                  onChange={(key) => {
                    setActiveTab(key);
                    changeTab(urlSyncEntityId, key as any);
                    // Close add task form when switching away from tasks tab
                    if (key !== 'tasks') {
                      setShouldOpenAddTaskForm(false);
                    }
                    // Close email editor when switching away from email tab
                    if (key !== 'email') {
                      setShouldOpenEmailEditor(false);
                    }
                    // Close add sequence form when switching away from sequences tab
                    if (key !== 'sequences') {
                      setShouldExpandAddSequence(false);
                    }
                  }}
                >
                  {hasPermission(Permissions.SEQUENCE_PROSPECT_ACTIVITY) && (
                    <TabPane
                      tab={<ProspectDetailsTabTitle tab="Activities" />}
                      key="activity"
                    >
                      {/* <ProspectActivity
                    prospectId={selectedProspectId}
                    fetchActivity={fetchActivity}
                    onActivityChange={onActivityChange}
                    activeTab={activeTab}
                  /> */}
                      <ProspectDetailActivity
                        key={getActiveTabContentKey('activity')}
                        prospectId={getProspectIdForTab('activity')}
                        fetchActivity={fetchActivity}
                        onActivityChange={onActivityChange}
                        activeTab={activeTab}
                      />
                    </TabPane>
                  )}
                  {hasPermission(Permissions.SEQUENCE_PROSPECT_ACTIVITY) && (
                    <TabPane
                      tab={
                        <ProspectDetailsTabTitle
                          tab="Notes"
                          count={Math.max(
                            0,
                            Number(prospectActivityCount?.notes) || 0,
                          )}
                        />
                      }
                      key="notes"
                    >
                      <NotesListTab
                        key={getActiveTabContentKey('notes')}
                        selectedProspectId={getProspectIdForTab('notes')}
                        onActivityChange={setFetchActivity}
                        fetchNotes={fetchNotes}
                        onNoteChange={onNoteChange}
                        activeTab={activeTab}
                        onTaskChange={onTaskChange}
                      />
                    </TabPane>
                  )}
                  {isDialerAvailable &&
                    hasPermission(Permissions.SEQUENCE_PROSPECT_ACTIVITY) && (
                      <TabPane
                        tab={
                          <ProspectDetailsTabTitle
                            tab="Calls"
                            count={Math.max(0, Number(callsTabCount) || 0)}
                          />
                        }
                        key="calls"
                      >
                        <CallsListTab
                          key={getActiveTabContentKey('calls')}
                          selectedProspectId={getProspectIdForTab('calls')}
                          activeTab={activeTab}
                          onCallsCountChange={setCallsTabCount}
                        />
                      </TabPane>
                    )}
                  {hasPermission(Permissions.SEQUENCE_PROSPECT_ACTIVITY) && (
                    <TabPane
                      tab={
                        <ProspectDetailsTabTitle
                          tab="Emails"
                          count={Math.max(
                            0,
                            Number(prospectActivityCount?.emails) || 0,
                          )}
                        />
                      }
                      key="email"
                    >
                      <EmailListTab
                        key={getActiveTabContentKey('email')}
                        selectedProspectId={getProspectIdForTab('email')}
                        selectedProspectData={singleProspectResponse}
                        initialShowEmailEditor={shouldOpenEmailEditor}
                        onEmailEditorClose={() =>
                          setShouldOpenEmailEditor(false)
                        }
                        onActivityChange={setFetchActivity}
                        activeTab={activeTab}
                      />
                    </TabPane>
                  )}

                  <TabPane
                    tab={
                      <ProspectDetailsTabTitle
                        tab="Tasks"
                        count={Math.max(
                          0,
                          Number(prospectActivityCount?.tasks) || 0,
                        )}
                      />
                    }
                    key="tasks"
                  >
                    <TasksListTab
                      key={getActiveTabContentKey('tasks')}
                      sendGetTasksRequest={sendGetTasksRequest}
                      getTasksRequestStatus={getTasksRequestStatus}
                      tasks={tasks}
                      selectedProspectId={getProspectIdForTab('tasks')}
                      sendUpdateTasksRequest={sendUpdateTasksRequest}
                      taskActionRequestStatus={taskActionRequestStatus}
                      taskActionRequestMessage={taskActionRequestMessage}
                      sendGetUndoTasksRequest={sendGetUndoTasksRequest}
                      getUndoTasksRequestStatus={getUndoTasksRequestStatus}
                      sendCreateTaskRequest={sendCreateTaskRequest}
                      createTaskRequestStatus={createTaskRequestStatus}
                      createTaskRequestMessage={createTaskRequestMessage}
                      createTaskRequestError={createTaskRequestError}
                      sendGetTaskAssigneeListRequest={
                        sendGetTaskAssigneeListRequest
                      }
                      taskAssigneeList={taskAssigneeList}
                      userId={userId}
                      sendUploadAttachmentRequest={sendUploadAttachmentRequest}
                      uploadAttachmentRequestStatus={
                        uploadAttachmentRequestStatus
                      }
                      uploadAttachmentRequestError={
                        uploadAttachmentRequestError
                      }
                      uploadedAttachment={uploadedAttachment}
                      sendDeleteAttachmentRequest={sendDeleteAttachmentRequest}
                      deleteAttachmentRequestStatus={
                        deleteAttachmentRequestStatus
                      }
                      deleteAttachmentRequestError={
                        deleteAttachmentRequestError
                      }
                      deletedAttachmentId={deletedAttachmentId}
                      sendImageUploadRequest={sendImageUploadRequest}
                      resetUploadAttachmentRequest={
                        resetUploadAttachmentRequest
                      }
                      resetDeleteAttachmentRequest={
                        resetDeleteAttachmentRequest
                      }
                      resetCreateTaskRequest={resetCreateTaskRequest}
                      sendGetShareableTaskerDataRequest={
                        sendGetShareableTaskerDataRequest
                      }
                      getShareableTaskerDataRequestStatus={
                        getShareableTaskerDataRequestStatus
                      }
                      shareableTaskerData={shareableTaskerData}
                      resetShareableTaskerState={resetShareableTaskerState}
                      sendGetWhatsappLinkRequest={sendGetWhatsappLinkRequest}
                      getWhatsappLinkRequestStatus={
                        getWhatsappLinkRequestStatus
                      }
                      getWhatsappLinkResponse={getWhatsappLinkResponse}
                      resetGetWhatsappLinkState={resetGetWhatsappLinkState}
                      setShowGlobalRestrictionModal={
                        setShowGlobalRestrictionModal
                      }
                      sendCreateProspectNoteRequest={
                        sendCreateProspectNoteRequest
                      }
                      createProspectNoteRequestStatus={
                        createProspectNoteRequestStatus
                      }
                      createProspectNoteRequestMessage={
                        createProspectNoteRequestMessage
                      }
                      createProspectNoteResponse={createProspectNoteResponse}
                      resetCreateProspectNotesListRequest={
                        resetCreateProspectNotesListRequest
                      }
                      sendUpdateProspectNoteRequest={
                        sendUpdateProspectNoteRequest
                      }
                      updateProspectNoteRequestStatus={
                        updateProspectNoteRequestStatus
                      }
                      resetUpdateProspectNoteRequest={
                        resetUpdateProspectNoteRequest
                      }
                      initialShowAddTaskForm={shouldOpenAddTaskForm}
                      onAddTaskFormClose={() => setShouldOpenAddTaskForm(false)}
                      onActivityChange={setFetchActivity}
                      onNoteChange={setFetchNotes}
                      activeTab={activeTab}
                      fetchTasks={fetchTasks}
                      onTaskChange={onTaskChange}
                      prospectActivityCount={prospectActivityCount}
                      editProspectAcitivityCount={editProspectAcitivityCount}
                      isDemoAccount={isDemoAccount}
                    />
                  </TabPane>

                  {hasPermission(Permissions.SEQUENCE_PROSPECT_SEQUENCES) && (
                    <TabPane
                      tab={
                        <ProspectDetailsTabTitle
                          tab="Sequences"
                          count={Math.max(
                            0,
                            Number(prospectActivityCount?.sequences) || 0,
                          )}
                        />
                      }
                      key="sequences"
                    >
                      <ProspectSequenceListTable
                        key={getActiveTabContentKey('sequences')}
                        selectedProspectSequences={selectedProspectSequences}
                        sendSequenceMarkAsRepliedRequest={
                          prospectSequenceMarkAsReplied
                        }
                        sendRemoveFromSequenceRequest={
                          prospectSequenceRemoveFromSequence
                        }
                        sendAddToStepRequest={prospectSequenceAddToStep}
                        sendGetSingleProspectSequencesDetailsRequest={
                          sendGetSingleProspectSequencesDetailsRequest
                        }
                        sendSequenceMarkAsFinishedRequest={
                          prospectSequenceMarkAsFinished
                        }
                        getSingleProspectSequencesDetailsStatus={
                          getSingleProspectSequencesDetailsStatus
                        }
                        isSingleProspectSubscribed={isSingleProspectSubscribed}
                        allOutcomes={allOutcomes}
                        getSequenceListRequest={getSequenceListRequest}
                        getStepListRequest={getStepListRequest}
                        addToStepRequestStatus={addToStepRequestStatus}
                        addToStepRequestSuccessfulCount={
                          addToStepRequestSuccessfulCount
                        }
                        addToStepRequestFailedCount={
                          addToStepRequestFailedCount
                        }
                        addToStepRequestMessage={addToStepRequestMessage}
                        initialIsAddSequenceExpanded={shouldExpandAddSequence}
                        onAddSequenceExpanded={() =>
                          setShouldExpandAddSequence(false)
                        }
                        onActivityChange={setFetchActivity}
                        prospectActivityCount={prospectActivityCount}
                        editProspectAcitivityCount={editProspectAcitivityCount}
                      />
                    </TabPane>
                  )}
                  {hasPermission(Permissions.SEQUENCE_PROSPECT_PROFILE) && (
                    <TabPane
                      tab={<ProspectDetailsTabTitle tab="All Fields" />}
                      key="contacts"
                    >
                      <ProspectProfileForm
                        key={getActiveTabContentKey('contacts')}
                        onClose={onHide}
                        selectedProspectId={selectedProspectId}
                        handleSaveContactAttributesSubmit={
                          handleSaveContactAttributesSubmit
                        }
                        selectedProspectData={singleProspectResponse}
                        getSingleProspectDetailsStatus={
                          getSingleProspectDetailsStatus
                        }
                        sendGetSingleProspectDetailsRequest={
                          sendGetSingleProspectDetailsRequest
                        }
                        saveContactAttributesRequestStatus={
                          saveContactAttributesRequestStatus
                        }
                        sendPrimaryEmailChangeInProspectRequest={
                          sendPrimaryEmailChangeInProspectRequest
                        }
                        sendPrimaryPhoneChangeInProspectRequest={
                          sendPrimaryPhoneChangeInProspectRequest
                        }
                        sendDeleteEmailFromProspectRequest={
                          sendDeleteEmailFromProspectRequest
                        }
                        sendDeletePhoneFromProspectRequest={
                          sendDeletePhoneFromProspectRequest
                        }
                        deleteSecondaryEmailRequestStatus={
                          deleteSecondaryEmailRequestStatus
                        }
                        resetProspectDeleteEmailRequestStatus={
                          resetProspectDeleteEmailRequestStatus
                        }
                        deleteSecondaryPhoneRequestStatus={
                          deleteSecondaryPhoneRequestStatus
                        }
                        resetProspectDeletePhoneRequestStatus={
                          resetProspectDeletePhoneRequestStatus
                        }
                        resetProspectPrimaryEmailChangeRequestStatus={
                          resetProspectPrimaryEmailChangeRequestStatus
                        }
                        resetUpdatePrimaryNumberState={
                          resetUpdatePrimaryNumberState
                        }
                        primaryEmailChangeRequestStatus={
                          primaryEmailChangeRequestStatus
                        }
                        primaryPhoneChangeRequestStatus={
                          primaryPhoneChangeRequestStatus
                        }
                        onActivityChange={setFetchActivity}
                        fields={fields}
                        prospectDetailAddField={true}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default withTranslation()(ProspectDetailsModal);
